import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Plus, Minus, Search } from "lucide-react";
import {
  filterCategories,
  filterSpecies,
  filterMoisture,
  filterProvinces,
  type MarketplaceFilters,
} from "@/lib/filterData";

interface Props {
  filters: MarketplaceFilters;
  onChange: (f: MarketplaceFilters) => void;
}

function toggle(arr: string[], val: string) {
  return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
}

function ExpandableList({
  items,
  limit = 4,
  renderItem,
}: {
  items: string[];
  limit?: number;
  renderItem: (item: string) => React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, limit);
  return (
    <>
      {visible.map(renderItem)}
      {items.length > limit && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-2 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
        >
          {expanded ? "Visa mindre" : "Visa alla"}
        </button>
      )}
    </>
  );
}

function Check({
  label,
  checked,
  onCheckedChange,
  indent = false,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  indent?: boolean;
}) {
  return (
    <label className={`flex cursor-pointer items-center gap-2 py-1 text-sm ${indent ? "pl-5" : ""}`}>
      <Checkbox checked={checked} onCheckedChange={onCheckedChange} />
      <span>{label}</span>
    </label>
  );
}

function RangeFilter({
  value,
  onChange,
  min,
  max,
  minLabel,
  maxLabel,
  maxDisplayLabel,
}: {
  value: [number, number];
  onChange: (v: [number, number]) => void;
  min: number;
  max: number;
  minLabel: string;
  maxLabel: string;
  maxDisplayLabel: string;
}) {
  return (
    <div className="space-y-3">
      <Slider
        min={min}
        max={max}
        step={max > 1000 ? 100 : 10}
        value={value}
        onValueChange={(v) => onChange(v as [number, number])}
      />
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="mb-1 block text-xs text-muted-foreground">{minLabel}</label>
          <Input
            type="number"
            min={min}
            max={max}
            value={value[0] === min ? "" : value[0]}
            placeholder={String(min)}
            onChange={(e) => {
              const n = e.target.value === "" ? min : Number(e.target.value);
              if (!isNaN(n)) onChange([Math.min(n, value[1]), value[1]]);
            }}
            className="h-8 text-sm"
          />
        </div>
        <div className="flex-1">
          <label className="mb-1 block text-xs text-muted-foreground">{maxLabel}</label>
          <div className="relative">
            <Input
              type="number"
              min={min}
              max={max}
              value={value[1] === max ? "" : value[1]}
              placeholder={String(max)}
              onChange={(e) => {
                const n = e.target.value === "" ? max : Number(e.target.value);
                if (!isNaN(n)) onChange([value[0], Math.max(n, value[0])]);
              }}
              className="h-8 text-sm"
            />
            {value[1] >= max && (
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {maxDisplayLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
function PlatsFilter({ filters, onChange }: Props) {
  const [platsSearch, setPlatsSearch] = useState("");
  const sc = "rounded-lg bg-muted/50 p-4";
  const query = platsSearch.toLowerCase().trim();

  // Filter provinces: show if province name matches OR any of its municipalities match
  const filteredProvinces = query
    ? filterProvinces.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.municipalities.some((m) => m.toLowerCase().includes(query))
      )
    : filterProvinces;

  return (
    <section className={sc}>
      <h3 className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">Plats</h3>
      <div className="relative mb-3">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Sök landskap eller kommun…"
          value={platsSearch}
          onChange={(e) => setPlatsSearch(e.target.value)}
          className="h-8 pl-8 text-sm"
        />
      </div>
      <ExpandableList
        items={filteredProvinces.map((p) => p.name)}
        renderItem={(name) => {
          const prov = filterProvinces.find((p) => p.name === name)!;
          const checked = filters.provinces.includes(name);
          // When searching, filter municipalities too
          const visibleMunis = query
            ? prov.municipalities.filter((m) => m.toLowerCase().includes(query)).sort((a, b) => a.localeCompare(b, "sv"))
            : prov.municipalities.slice().sort((a, b) => a.localeCompare(b, "sv"));
          return (
            <div key={name}>
              <label className="flex cursor-pointer items-center gap-2 py-1 text-sm">
                <Checkbox
                  checked={checked}
                  onCheckedChange={() =>
                    onChange({
                      ...filters,
                      provinces: toggle(filters.provinces, name),
                      municipalities: checked
                        ? filters.municipalities.filter((m) => !prov.municipalities.includes(m))
                        : filters.municipalities,
                    })
                  }
                />
                <span className="flex-1">{name}</span>
                {prov.municipalities.length > 0 &&
                  (checked ? <Minus size={14} className="text-muted-foreground" /> : <Plus size={14} className="text-muted-foreground" />)}
              </label>
              {checked &&
                visibleMunis.map((mun) => (
                  <Check key={mun} label={mun} checked={filters.municipalities.includes(mun)} onCheckedChange={() => onChange({ ...filters, municipalities: toggle(filters.municipalities, mun) })} indent />
                ))}
            </div>
          );
        }}
      />
    </section>
  );
}

export default function MarketplaceFiltersSidebar({ filters, onChange }: Props) {
  const hasAny =
    filters.categories.length > 0 ||
    filters.subCategories.length > 0 ||
    filters.species.length > 0 ||
    filters.moisture.length > 0 ||
    filters.provinces.length > 0 ||
    filters.municipalities.length > 0 ||
    filters.priceRange[0] > 0 ||
    filters.priceRange[1] < 50000 ||
    filters.ageRange[0] > 0 ||
    filters.ageRange[1] < 600;

  const clearAll = () =>
    onChange({
      ...filters,
      categories: [],
      subCategories: [],
      species: [],
      moisture: [],
      provinces: [],
      municipalities: [],
      priceRange: [0, 50000],
      ageRange: [0, 600],
    });

  const sc = "rounded-lg bg-muted/50 p-4";

  return (
    <div className="space-y-3 font-sans">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Filter</h2>
        {hasAny && (
          <button type="button" onClick={clearAll} className="text-xs text-primary hover:underline">
            Rensa alla filter
          </button>
        )}
      </div>

      <section className={sc}>
        <h3 className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">Kategorier</h3>
        <ExpandableList
          items={filterCategories.map((c) => c.value)}
          renderItem={(val) => {
            const cat = filterCategories.find((c) => c.value === val)!;
            const checked = filters.categories.includes(val);
            return (
              <div key={val}>
                <label className="flex cursor-pointer items-center gap-2 py-1 text-sm">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() =>
                      onChange({
                        ...filters,
                        categories: toggle(filters.categories, val),
                        subCategories: checked
                          ? filters.subCategories.filter((s) => !cat.subs.includes(s))
                          : filters.subCategories,
                      })
                    }
                  />
                  <span className="flex-1">{cat.label}</span>
                  {cat.subs.length > 0 &&
                    (checked ? <Minus size={14} className="text-muted-foreground" /> : <Plus size={14} className="text-muted-foreground" />)}
                </label>
                {checked &&
                  cat.subs.length > 0 &&
                  cat.subs.slice().sort((a, b) => a.localeCompare(b, "sv")).map((sub) => (
                    <Check key={sub} label={sub} checked={filters.subCategories.includes(sub)} onCheckedChange={() => onChange({ ...filters, subCategories: toggle(filters.subCategories, sub) })} indent />
                  ))}
              </div>
            );
          }}
        />
      </section>

      <section className={sc}>
        <h3 className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">Trädslag</h3>
        <ExpandableList
          items={filterSpecies}
          renderItem={(s) => (
            <Check key={s} label={s} checked={filters.species.includes(s)} onCheckedChange={() => onChange({ ...filters, species: toggle(filters.species, s) })} />
          )}
        />
      </section>

      <section className={sc}>
        <h3 className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">Fuktkvot</h3>
        {filterMoisture.map((m) => (
          <Check key={m} label={m} checked={filters.moisture.includes(m)} onCheckedChange={() => onChange({ ...filters, moisture: toggle(filters.moisture, m) })} />
        ))}
      </section>

      <PlatsFilter filters={filters} onChange={onChange} />

      <section className={sc}>
        <h3 className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pris</h3>
        <RangeFilter value={filters.priceRange} onChange={(v) => onChange({ ...filters, priceRange: v })} min={0} max={50000} minLabel="Min kr" maxLabel="Max kr" maxDisplayLabel="50 000+" />
      </section>

      <section className={sc}>
        <h3 className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">Ålder</h3>
        <RangeFilter value={filters.ageRange} onChange={(v) => onChange({ ...filters, ageRange: v })} min={0} max={600} minLabel="Min år" maxLabel="Max år" maxDisplayLabel="600+" />
      </section>
    </div>
  );
}
